import { useState, useEffect, useCallback, useRef } from 'react';
import { spatialApi, projectsApi, datasetsApi } from '../lib/api';
import { DEMO_LAYERS, getProjectCenter, explodeMultiGeometries } from '../lib/mapViewHelpers';

/**
 * Data loading for MapView: project lookup, USGS demo GeoJSON, and
 * viewport-debounced PostGIS feature loads.
 */
export function useMapData({ searchParams, mapRefHolder, demoMode = true }) {
  const [projectCenter, setProjectCenter] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [usgsData, setUsgsData] = useState({});
  const [viewport, setViewport] = useState(null);

  // ── Viewport-based DB feature loading (PostGIS features_in_view RPC) ──
  const [dbFeatures, setDbFeatures] = useState(null); // { points: FC, lines: FC } | null
  const dbFetchTimer = useRef(null);
  const dbLastBbox = useRef('');
  const dbSpatialAvailable = useRef(true); // stop retrying if migration 010 isn't applied
  const mountedRef = useRef(true);

  // M6: stop the debounce timer and ignore late responses once unmounted.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (dbFetchTimer.current) clearTimeout(dbFetchTimer.current);
      dbFetchTimer.current = null;
    };
  }, []);

  const fetchViewportFeatures = useCallback(() => {
    const map = mapRefHolder.current?.current?.getMap?.();
    if (!map || !dbSpatialAvailable.current) return;
    const b = map.getBounds();
    // Skip duplicate fetches for effectively-identical viewports
    const key = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].map(v => v.toFixed(3)).join(',');
    if (key === dbLastBbox.current) return;
    dbLastBbox.current = key;

    spatialApi.featuresInView(b.getWest(), b.getSouth(), b.getEast(), b.getNorth())
      .then(res => {
        if (!mountedRef.current) return; // M6: ignore responses after unmount
        const fc = res.data;
        if (!fc?.features) return;
        // MapLibre clustering requires a point-only source — split geometry types
        const points = fc.features.filter(f => f.geometry?.type === 'Point');
        const lines = fc.features.filter(f => f.geometry?.type !== 'Point');
        setDbFeatures(points.length || lines.length ? {
          points: { type: 'FeatureCollection', features: points },
          lines: { type: 'FeatureCollection', features: lines },
        } : null);
      })
      .catch(err => {
        // 500 with "migration 010" hint, or auth issues — stop polling quietly
        if (err.response?.status >= 500) dbSpatialAvailable.current = false;
      });
  }, [mapRefHolder]);

  // Debounced moveend → fetch (400ms after the map settles)
  const handleViewportChange = useCallback((vs) => {
    setViewport(vs);
    if (dbFetchTimer.current) clearTimeout(dbFetchTimer.current);
    dbFetchTimer.current = setTimeout(fetchViewportFeatures, 400);
  }, [fetchViewportFeatures]);

  // Initial load once the map ref is available
  useEffect(() => {
    const t = setTimeout(fetchViewportFeatures, 1500);
    return () => {
      clearTimeout(t);
      if (dbFetchTimer.current) clearTimeout(dbFetchTimer.current);
    };
  }, [fetchViewportFeatures]);

  // Load project data from URL params
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (!projectId) return undefined;
    let active = true;
    projectsApi.get(projectId)
      .then(res => {
        if (!active) return;
        const center = getProjectCenter(res.data);
        if (center) {
          setProjectCenter(center);
        }
        // Store project info to determine whether to show demo data
        setCurrentProject(res.data);
      })
      .catch(err => { if (active) console.warn('Failed to load project:', err); });
    return () => { active = false; };
  }, [searchParams]);

  // `?dataset=` without `?project=`: the dataset names the project (its id, organisation and name),
  // so the Report Builder can save to the server and title itself after the dataset (V.28, V.52).
  // Kept apart from currentProject, which also drives demo data and the map centre.
  const [datasetContext, setDatasetContext] = useState({ datasetName: null, project: null });
  useEffect(() => {
    const datasetId = searchParams.get('dataset');
    setDatasetContext({ datasetName: null, project: null });
    if (!datasetId || !datasetsApi?.get) return undefined;
    let active = true;
    datasetsApi.get(datasetId)
      .then(async (res) => {
        const ds = res?.data || {};
        if (!active) return;
        setDatasetContext({ datasetName: ds.name || null, project: ds.project_id ? { id: ds.project_id, organization_id: ds.organization_id || null, name: null } : null });
        if (!ds.project_id) return;
        try {
          const p = await projectsApi.get(ds.project_id);
          if (active && p?.data) setDatasetContext({ datasetName: ds.name || null, project: p.data });
        } catch { /* keep the id-only project */ }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [searchParams]);

  // Load real USGS GeoJSON data files — only for Cape Cod project or when no project specified.
  // M5: the effect cleanup aborts in-flight fetches and flips `active` so a response
  // from a previous project can never repopulate state or fire after unmount.
  useEffect(() => {
    const projectId = searchParams.get('project');
    // Demo data is opt-in (Demo mode), and never for a non-Cape Cod project
    if (!demoMode) {
      setUsgsData(prev => (Object.keys(prev).length ? {} : prev));
      return undefined;
    }
    if (currentProject && !currentProject.location?.toLowerCase().includes('cape cod')) {
      setUsgsData({});
      return undefined;
    }
    // Project requested but not resolved yet: wait for it
    if (projectId && !currentProject) return undefined;

    let active = true;
    const controller = new AbortController();
    DEMO_LAYERS.forEach(layer => {
      if (!layer.geojsonUrl) return;
      fetch(layer.geojsonUrl, { signal: controller.signal })
        .then(r => r.json())
        .then(geojson => {
          if (!active) return;
          explodeMultiGeometries(geojson);
          setUsgsData(prev => ({ ...prev, [layer.id]: geojson }));
        })
        .catch(err => {
          if (!active || err?.name === 'AbortError') return;
          console.warn(`Failed to load ${layer.geojsonUrl}:`, err);
        });
    });
    return () => { active = false; controller.abort(); };
  }, [searchParams, currentProject, demoMode]);

  return {
    projectCenter, setProjectCenter, currentProject, usgsData, datasetContext,
    dbFeatures, viewport, handleViewportChange,
  };
}
